// Google Analytics (GA4, gtag.js) usage tracking for the rider self-service
// score-sheet portal (#948, reported via bugs board #95). This is a public,
// no-auth app (#621) that emails riders a permanent private link containing
// an opaque token/requestId — that token is itself sensitive (it's what lets
// anyone who has it view a rider's scores), so it must never be sent to a
// third party like Google. This module therefore:
//   - only ever sends ROUTE TEMPLATES ("/sheets/:token"), never the real
//     token/requestId/rideId, as `page_path`, and never sends `page_location`
//     (which would otherwise carry the real URL);
//   - only ever sends generic, non-identifying event params (e.g. a variant
//     name or class number) — never rider name, email, CNO, or any id that
//     could re-identify a specific person's request.
//
// Config-driven and safe-by-default: with no VITE_GA_MEASUREMENT_ID set (any
// local/dev/CI build that hasn't configured one), every export here is a
// no-op — nothing loads, nothing is queued, nothing is sent.
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

let initialized = false;

function loadGtagScript() {
  if (document.getElementById("ga4-gtag-js")) return;
  const script = document.createElement("script");
  script.id = "ga4-gtag-js";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);
}

// Call once, at app startup. No-ops (and is safe to call repeatedly) when
// VITE_GA_MEASUREMENT_ID isn't configured.
export function initAnalytics() {
  if (!MEASUREMENT_ID || initialized) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args) {
    window.dataLayer.push(args);
  };
  window.gtag("js", new Date());
  // `anonymize_ip` is GA4's default. `send_page_view: false` because this is
  // a client-routed SPA (react-router) — we send page_view ourselves on each
  // route change (see trackPageView) instead of only once at initial load.
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false });

  loadGtagScript();
}

// Collapses a real pathname (which may contain a rider's private token/ids)
// down to its react-router route template, so nothing identifying is ever
// sent as `page_path`. Keep in sync with the routes declared in App.jsx.
export function routeTemplate(pathname) {
  if (pathname === "/") return "/";
  if (/^\/sheets\/[^/]+$/.test(pathname)) return "/sheets/:token";
  if (/^\/open\/[^/]+\/[^/]+\/[^/]+$/.test(pathname)) {
    return "/open/:requestId/:rideId/:variant";
  }
  return "/other";
}

// Send a page_view for the given real pathname, translated to its route
// template first. No-ops when GA isn't configured.
export function trackPageView(pathname) {
  if (!MEASUREMENT_ID || typeof window.gtag !== "function") return;
  const template = routeTemplate(pathname);
  window.gtag("event", "page_view", {
    page_path: template,
    page_title: template,
  });
}

// Generic event helper. `params` must never contain PII (rider name, email,
// CNO, rideId, requestId/token) — only non-identifying context such as a
// variant name or class number. No-ops when GA isn't configured.
export function trackEvent(name, params = {}) {
  if (!MEASUREMENT_ID || typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
}
